// Pure helpers for the dataset viewer: value parsing, column type detection,
// column filters, the filter expression language and sorting.

const DAY = 86400000;
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const NUMBER_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i;
// 21-Aug-2026, 21 Aug 2026 | 10:30, UNK-UNK-2022, 21/Aug/2026
const DMY_RE = /^(\d{1,2}|unk?)[-\s/]([a-z]{3,9}|unk?)[-\s/](\d{4}|unkn?)(?:\s*\|?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/i;
// 2026-08-21, 2026-08-21T10:30:00Z
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?\S*)?$/;

export const cellText = (v) => (v == null ? "" : String(v));

export const isBlank = (v) => cellText(v).trim() === "";

export const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  const s = cellText(v).trim();
  return NUMBER_RE.test(s) ? Number(s) : NaN;
};

const parseDateParts = (v) => {
  const s = cellText(v).trim();
  let m = s.match(ISO_RE);
  if (m) return { y: m[1], mo: String(+m[2] - 1), d: m[3], h: m[4], mi: m[5], sec: m[6] };
  m = s.match(DMY_RE);
  if (!m) return null;
  const mon = m[2].slice(0, 3).toLowerCase();
  if (!/^unk?$/i.test(m[2]) && !(mon in MONTHS)) return null;
  return { y: m[3], mo: mon in MONTHS ? String(MONTHS[mon]) : "UNK", d: m[1], h: m[4], mi: m[5], sec: m[6] };
};

// Recognised date (full or partial with UNK day/month/year)
export const isDateLike = (v) => !!parseDateParts(v);

export const hasTime = (v) => { const p = parseDateParts(v); return !!(p && p.h != null); };

// Milliseconds (UTC) of a date value. Partial dates (UNK parts) give NaN unless `impute`
// is set, in which case unknown day / month are taken as the first one (sorting only).
export const toDate = (v, impute = false) => {
  const p = parseDateParts(v);
  if (!p) return NaN;
  const unk = (x) => /^un/i.test(x);
  if (unk(p.y)) return NaN;
  if ((unk(p.mo) || unk(p.d)) && !impute) return NaN;
  const y = +p.y, mo = unk(p.mo) ? 0 : +p.mo, d = unk(p.d) ? 1 : +p.d;
  const ms = Date.UTC(y, mo, d, +(p.h || 0), +(p.mi || 0), +(p.sec || 0));
  const chk = new Date(ms);
  if (chk.getUTCFullYear() !== y || chk.getUTCMonth() !== mo || chk.getUTCDate() !== d) return NaN;
  return ms;
};

// "numeric" when every non-blank value is a number, "date" when every non-blank value is a date
export const inferColumnTypes = (rows, columns) => {
  const types = {};
  columns.forEach((col) => {
    let numeric = true, date = true, any = false;
    for (const row of rows) {
      const v = row?.fieldRow?.[col];
      if (isBlank(v)) continue;
      any = true;
      if (numeric && isNaN(toNumber(v))) numeric = false;
      if (date && !isDateLike(v)) date = false;
      if (!numeric && !date) break;
    }
    types[col] = !any ? "character" : numeric ? "numeric" : date ? "date" : "character";
  });
  return types;
};

// Compares a cell with a typed value: number with number, date with date (whole days when the
// value has no time), otherwise case-insensitive text. null = not comparable (e.g. text vs number).
export const compareValue = (cell, value) => {
  const s = cellText(cell).trim();
  const v = cellText(value).trim();
  const textEqual = s.toLowerCase() === v.toLowerCase();
  const vn = toNumber(v);
  if (!isNaN(vn)) {
    const sn = toNumber(s);
    if (!isNaN(sn)) return Math.sign(sn - vn);
    return textEqual ? 0 : null;
  }
  const vd = toDate(v);
  if (!isNaN(vd)) {
    const sd = toDate(s);
    if (isNaN(sd)) return textEqual ? 0 : null;
    return hasTime(v) ? Math.sign(sd - vd) : Math.sign(Math.floor(sd / DAY) - Math.floor(vd / DAY));
  }
  if (textEqual) return 0;
  const c = s.toLowerCase().localeCompare(v.toLowerCase(), undefined, { numeric: true });
  return c ? Math.sign(c) : (s.toLowerCase() < v.toLowerCase() ? -1 : 1);
};

// ---------------------------------------------------------------------------
// Column filters ("Filter Column…")
// ---------------------------------------------------------------------------

export const COLUMN_FILTER_OPS = {
  numeric: [["eq", "Equals"], ["ne", "Not equals"], ["gt", "Greater than"], ["lt", "Less than"], ["between", "Between"], ["contains", "Contains"], ["blank", "Is blank"], ["nonblank", "Is not blank"]],
  date: [["before", "Before"], ["after", "After"], ["dbetween", "Between"], ["eq", "Equals"], ["contains", "Contains"], ["blank", "Is blank"], ["nonblank", "Is not blank"]],
  character: [["contains", "Contains"], ["eq", "Equals"], ["ne", "Not equals"], ["starts", "Starts with"], ["ends", "Ends with"], ["blank", "Is blank"], ["nonblank", "Is not blank"]]
};

export const defaultColumnFilterOp = (type) => (type === "character" || !COLUMN_FILTER_OPS[type] ? "contains" : "eq");

const isRangeOp = (op) => op === "between" || op === "dbetween";

export const isColumnFilterActive = (f) =>
  !!f && (f.op === "blank" || f.op === "nonblank" || cellText(f.val).trim() !== "" || (isRangeOp(f.op) && cellText(f.val2).trim() !== ""));

// Error message for an incomplete / invalid column filter, "" when it can be applied
export const validateColumnFilter = (f) => {
  if (!f || f.op === "blank" || f.op === "nonblank") return "";
  const v1 = cellText(f.val).trim(), v2 = cellText(f.val2).trim();
  if (["gt", "lt", "between"].includes(f.op)) {
    if (isNaN(toNumber(v1)) || (f.op === "between" && isNaN(toNumber(v2)))) {
      return f.op === "between" ? "Enter two numbers." : "Enter a number.";
    }
  }
  if (["before", "after", "dbetween"].includes(f.op)) {
    if (isNaN(toDate(v1)) || (f.op === "dbetween" && isNaN(toDate(v2)))) {
      return f.op === "dbetween" ? "Enter two dates, e.g. 01-Jan-2026 and 31-Dec-2026." : "Enter a date, e.g. 21-Aug-2026.";
    }
  }
  return "";
};

export const columnFilterLabel = (f) => {
  const m = {
    contains: "contains", eq: "=", ne: "≠", starts: "starts with", ends: "ends with",
    gt: ">", lt: "<", between: "between", before: "before", after: "after",
    dbetween: "between", blank: "is blank", nonblank: "is not blank"
  };
  if (f.op === "blank" || f.op === "nonblank") return m[f.op];
  if (isRangeOp(f.op)) return `${m[f.op]} ${f.val} .. ${f.val2}`;
  return `${m[f.op] || f.op} ${f.val}`;
};

export const matchColumnFilter = (raw, f, type) => {
  const s = cellText(raw).trim();
  if (f.op === "blank") return s === "";
  if (f.op === "nonblank") return s !== "";
  const val = cellText(f.val).trim();
  const sl = s.toLowerCase(), vl = val.toLowerCase();
  switch (f.op) {
    case "contains": return sl.includes(vl);
    case "starts": return sl.startsWith(vl);
    case "ends": return sl.endsWith(vl);
    case "eq":
    case "ne": {
      const eq = type === "character" ? sl === vl : (s !== "" && compareValue(s, val) === 0);
      return f.op === "eq" ? eq : !eq;
    }
    case "gt":
    case "lt":
    case "between": {
      const n = toNumber(s);
      if (isNaN(n)) return false;
      const a = toNumber(val);
      if (f.op === "gt") return n > a;
      if (f.op === "lt") return n < a;
      const b = toNumber(f.val2);
      return n >= Math.min(a, b) && n <= Math.max(a, b);
    }
    case "before":
    case "after":
    case "dbetween": {
      if (s === "" || isNaN(toDate(s))) return false;
      const c1 = compareValue(s, val);
      if (f.op === "before") return c1 === -1;
      if (f.op === "after") return c1 === 1;
      const c2 = compareValue(s, f.val2);
      const [lo, hi] = toDate(val) <= toDate(f.val2) ? [c1, c2] : [c2, c1];
      return lo !== null && hi !== null && lo >= 0 && hi <= 0;
    }
    default: return true;
  }
};

// ---------------------------------------------------------------------------
// Filter expression, e.g.  AEYN == "Yes" and (AGE >= 18 or GENDER = 'FEMALE')
// ---------------------------------------------------------------------------

const EXPR_OPS = ["==", "!=", ">=", "<=", "<>", "=", ">", "<"];

const tokenize = (expr) => {
  const tokens = [];
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (/\s/.test(ch)) { i++; continue; }
    if (ch === "(" || ch === ")") { tokens.push({ type: ch }); i++; continue; }
    if (ch === '"' || ch === "'") {
      const end = expr.indexOf(ch, i + 1);
      if (end < 0) throw new Error(`Missing closing ${ch} quote.`);
      tokens.push({ type: "str", value: expr.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    let op = null;
    for (const o of EXPR_OPS) {
      if (expr.startsWith(o, i)) { op = o; break; }
    }
    if (op) {
      tokens.push({ type: "op", value: op === "=" ? "==" : op === "<>" ? "!=" : op });
      i += op.length;
      continue;
    }
    let j = i;
    while (j < expr.length && !/[\s()=<>]/.test(expr[j]) && !(expr[j] === "!" && expr[j + 1] === "=")) j++;
    const word = expr.slice(i, j);
    tokens.push(/^(and|or)$/i.test(word) ? { type: word.toLowerCase() } : { type: "word", value: word });
    i = j;
  }
  return tokens;
};

const evalCondition = (cell, op, value) => {
  const s = cellText(cell).trim();
  if (op === "==" || op === "!=") {
    const eq = value.trim() === "" ? s === "" : (s !== "" && compareValue(s, value) === 0);
    return op === "==" ? eq : !eq;
  }
  if (s === "") return false;
  const c = compareValue(s, value);
  if (c === null) return false;
  switch (op) {
    case ">": return c > 0;
    case "<": return c < 0;
    case ">=": return c >= 0;
    case "<=": return c <= 0;
    default: return false;
  }
};

// Parses the expression once. Returns predicate(row, searchColumns) or throws an Error with a
// user readable message. "and" binds tighter than "or"; parentheses group. A condition without
// an operator is a case-insensitive text search over `searchColumns`.
export const parseFilterExpression = (expr, columns) => {
  const tokens = tokenize(expr);
  let pos = 0;
  const peek = () => tokens[pos];
  const describe = (t) => (t.value !== undefined ? t.value : t.type);

  const readValue = () => {
    const t = peek();
    if (t && t.type === "str") { pos++; return t.value; }
    const words = [];
    while (peek() && peek().type === "word") { words.push(peek().value); pos++; }
    return words.length ? words.join(" ") : null;
  };

  const parseCondition = () => {
    const lhs = readValue();
    if (peek() && peek().type === "op") {
      const op = tokens[pos++].value;
      const colName = columns.find(c => c.toLowerCase() === lhs.trim().toLowerCase());
      if (!colName) throw new Error(`Unknown column: ${lhs}`);
      const value = readValue();
      if (value === null) throw new Error(`Missing value after "${colName} ${op}". Use "" to test for blank.`);
      if (peek() && peek().type === "op") throw new Error(`Unexpected "${peek().value}" after "${colName} ${op} ${value}". Quote values that contain operators.`);
      return (row) => evalCondition(row?.fieldRow?.[colName], op, value);
    }
    const text = lhs.toLowerCase();
    return (row, searchColumns) => searchColumns.some(c => cellText(row?.fieldRow?.[c]).toLowerCase().includes(text));
  };

  const parseTerm = () => {
    const t = peek();
    if (!t) throw new Error("Incomplete expression: a condition is missing at the end.");
    if (t.type === "(") {
      pos++;
      const inner = parseOr();
      if (!peek() || peek().type !== ")") throw new Error("Missing closing parenthesis.");
      pos++;
      return inner;
    }
    if (t.type === ")") throw new Error('Unexpected ")".');
    if (t.type === "and" || t.type === "or") throw new Error(`Unexpected "${t.type}": a condition is missing before it.`);
    if (t.type === "op") throw new Error(`Missing column name before "${t.value}".`);
    return parseCondition();
  };

  const parseAnd = () => {
    let left = parseTerm();
    while (peek() && peek().type === "and") {
      pos++;
      const l = left, r = parseTerm();
      left = (row, cols) => l(row, cols) && r(row, cols);
    }
    return left;
  };

  function parseOr() {
    let left = parseAnd();
    while (peek() && peek().type === "or") {
      pos++;
      const l = left, r = parseAnd();
      left = (row, cols) => l(row, cols) || r(row, cols);
    }
    return left;
  }

  const predicate = parseOr();
  if (pos < tokens.length) {
    const t = tokens[pos];
    throw new Error(t.type === ")" ? 'Unexpected ")".' : `Unexpected "${describe(t)}": join conditions with and / or.`);
  }
  return predicate;
};

// ---------------------------------------------------------------------------
// Sorting: blanks first (ascending), then by column type
// ---------------------------------------------------------------------------

export const compareCells = (a, b, type) => {
  const ea = isBlank(a), eb = isBlank(b);
  if (ea || eb) return ea && eb ? 0 : (ea ? -1 : 1);
  if (type === "numeric" || type === "date") {
    const x = type === "numeric" ? toNumber(a) : toDate(a, true);
    const y = type === "numeric" ? toNumber(b) : toDate(b, true);
    if (isNaN(x) !== isNaN(y)) return isNaN(x) ? -1 : 1;
    if (!isNaN(x) && x !== y) return x - y;
    if (!isNaN(x)) return 0;
  }
  return cellText(a).localeCompare(cellText(b), undefined, { numeric: true, sensitivity: "base" });
};
